import React, { useId } from "react";

/**
 * The lens on the eye as seen at the slit lamp (examiner's view): the iris
 * where the Pentacam traced the limbus, the pupil where it measured it, and
 * the lens at its diameter with each quadrant's steps at the rotation the
 * order gives. Drawn to scale.
 *
 * Angles are the map's: 0 deg at 3 o'clock, counter-clockwise. For a right
 * eye nasal is toward 0 deg, for a left eye toward 180 deg.
 */

const W = 640;
const H = 540;
const K = 25;                       // px per mm
const CX = W / 2;
const CY = H / 2 - 6;
const DEG = Math.PI / 180;

const X = (x) => CX + x * K;
const Y = (y) => CY - y * K;
const pt = (r, a, ox = 0, oy = 0) => [X(ox + r * Math.cos(a * DEG)), Y(oy + r * Math.sin(a * DEG))];

// the theme's: slate for the steps, as the figures beside it; the lens edge in the main page's orange
const STEP_FILL = "#414f62";
const STD_FILL = "#eef1f4";
const LENS = "#414f62";
const LENS_EDGE = "#f5891d";

/** Conjunctival vessels: a few strands running in from the edge of the view toward the limbus. */
const VESSELS = [
  [200, 12.6, 8.6, 18], [160, 12.2, 8.2, -14], [215, 11.8, 8.0, 10], [335, 12.4, 8.3, -16],
  [20, 12.0, 8.4, 14], [350, 12.8, 9.0, 8], [80, 10.4, 8.4, -10], [280, 10.2, 8.6, 12],
  [120, 11.6, 8.8, 16], [250, 11.4, 8.5, -12], [55, 11.8, 9.0, -8], [305, 12.0, 9.2, 10],
];

export default function EyeGraphic({ side, diameter, lcc, midperipheral, steps, steep, flat, limbus, pupil, hwtw, dots }) {
  const id = useId().replace(/:/g, "");
  const known = side === "OD" || side === "OS";
  const n = side === "OS" ? -1 : 1;
  const rEdge = diameter / 2;
  const rLcc = (lcc || diameter - 3) / 2;
  const rMp = (midperipheral || diameter - 4.6) / 2;
  const iris = limbus
    ? { x: limbus.cx, y: limbus.cy, r: limbus.d / 2 }
    : { x: 0, y: 0, r: (typeof hwtw === "number" && hwtw > 9 ? hwtw : 11.8) / 2 };
  const pup = pupil || { x: 0, y: 0, d: 3.5 };

  const chips = (steps || []).map((q, i) => {
    const [x, y] = pt((rLcc + rEdge) / 2, q.centre);
    const std = q.steps === "STD";
    return (
      <g key={`c${i}`} className="eye-chip">
        <rect x={x - 25} y={y - 13} width={50} height={26} rx={8} fill={std ? STD_FILL : STEP_FILL}
              stroke="#fff" strokeWidth={1.5} />
        <text x={x} y={y + 5} textAnchor="middle" fill={std ? "#57626c" : "#fff"}>{q.steps}</text>
      </g>
    );
  });

  const boundaries = (steps || []).map((q, i) => {
    const a = q.centre + 45;
    const [x0, y0] = pt(rLcc, a), [x1, y1] = pt(rEdge, a);
    return <line key={`b${i}`} x1={x0} y1={y0} x2={x1} y2={y1} stroke={LENS} strokeOpacity={0.55} strokeWidth={1.2} />;
  });

  const meridian = (angle, label, key) => {
    if (angle == null) return null;
    const lines = [angle, angle + 180].map((a) => {
      const [x0, y0] = pt(rMp - 0.4, a), [x1, y1] = pt(rEdge + 0.7, a);
      return <line key={`${key}${a}`} x1={x0} y1={y0} x2={x1} y2={y1} />;
    });
    // the label goes at the upper end; a near-horizontal meridian's goes at the temporal end
    const nearHorizontal = Math.abs(Math.sin(angle * DEG)) < 0.5;
    const a = nearHorizontal
      ? (Math.cos(angle * DEG) * n < 0 ? angle : angle + 180)
      : (Math.sin(angle * DEG) >= 0 ? angle : angle + 180);
    const [lx, ly] = pt(rEdge + 1.2, a);
    const anchor = Math.cos(a * DEG) > 0.3 ? "start" : Math.cos(a * DEG) < -0.3 ? "end" : "middle";
    const dy = Math.sin(a * DEG) > 0.3 ? 0 : Math.sin(a * DEG) < -0.3 ? 10 : 5;
    return (
      <g key={key} className={`eye-meridian eye-meridian-${key}`}>
        {lines}
        <text x={lx} y={ly + dy} textAnchor={anchor}>{label}</text>
      </g>
    );
  };

  const fibres = Array.from({ length: 64 }, (_, i) => i * 5.625).map((a, i) => {
    const [x0, y0] = pt(pup.d / 2 + 0.3, a, pup.x, pup.y), [x1, y1] = pt(iris.r - 0.4, a + (i % 2 ? 4 : -4), iris.x, iris.y);
    return <line key={`f${i}`} x1={x0} y1={y0} x2={x1} y2={y1} />;
  });

  const vessels = VESSELS.map(([a, r0, r1, bend], i) => {
    const [x0, y0] = pt(r0, a), [x1, y1] = pt(r1, a + bend * 0.25);
    const [qx, qy] = pt((r0 + r1) / 2, a + bend * 0.6);
    const [bx, by] = pt((r0 + r1) / 2 + 0.6, a + bend * 0.9 + 6);
    return (
      <g key={`v${i}`}>
        <path d={`M${x0} ${y0}Q${qx} ${qy} ${x1} ${y1}`} />
        <path d={`M${qx} ${qy}L${bx} ${by}`} strokeWidth={0.6} />
      </g>
    );
  });

  return (
    <svg viewBox={`0 0 ${W} ${H}`} className="eye-svg" role="img"
         aria-label={`The ${diameter.toFixed(1)} mm lens on the ${side === "OS" ? "left" : side === "OD" ? "right" : ""} eye, examiner's view`}>
      <defs>
        <radialGradient id={`sclera${id}`} cx={`${(X(0) / W) * 100}%`} cy={`${(Y(0) / H) * 100}%`} r="62%">
          <stop offset="35%" stopColor="#fbf8f4" />
          <stop offset="80%" stopColor="#efe5db" />
          <stop offset="100%" stopColor="#e2d3c5" />
        </radialGradient>
        <radialGradient id={`iris${id}`}>
          <stop offset="0%" stopColor="#3e5260" />
          <stop offset="55%" stopColor="#6c8595" />
          <stop offset="100%" stopColor="#4d6271" />
        </radialGradient>
        <radialGradient id={`lens${id}`}>
          <stop offset="70%" stopColor="#8b9bb0" stopOpacity={0.06} />
          <stop offset="100%" stopColor="#64758c" stopOpacity={0.22} />
        </radialGradient>
      </defs>

      <rect x={0} y={0} width={W} height={H} rx={16} fill={`url(#sclera${id})`} />
      <g stroke="#c96a6a" strokeOpacity={0.3} strokeWidth={1} fill="none" strokeLinecap="round">{vessels}</g>

      <circle cx={X(iris.x)} cy={Y(iris.y)} r={iris.r * K} fill={`url(#iris${id})`} />
      <g stroke="#9fb3bf" strokeOpacity={0.35} strokeWidth={0.8}>{fibres}</g>
      <circle cx={X(iris.x)} cy={Y(iris.y)} r={iris.r * K} fill="none" stroke="#33434e" strokeWidth={2.4} strokeOpacity={0.8} />
      <circle cx={X(pup.x)} cy={Y(pup.y)} r={(pup.d / 2) * K} fill="#0d1114" />
      <ellipse cx={X(-0.9)} cy={Y(1.3)} rx={0.55 * K} ry={0.35 * K} fill="#fff" opacity={0.85} />

      {/* the lens */}
      <circle cx={X(0)} cy={Y(0)} r={rEdge * K} fill={`url(#lens${id})`} stroke={LENS_EDGE} strokeWidth={2.2} />
      <circle cx={X(0)} cy={Y(0)} r={rLcc * K} fill="none" stroke={LENS} strokeOpacity={0.5} strokeWidth={1} strokeDasharray="4 3" />
      {boundaries}
      {(dots || []).map((d) => {
        const [x, y] = pt(rLcc, d.angle);
        return (
          <circle key={`d${d.angle}`} cx={x} cy={y} r={3} fill="#2b3644">
            <title>{`Drill dot ${d.clearance.toFixed(2)} mm outside the limbus`}</title>
          </circle>
        );
      })}

      {meridian(steep, `Steep ${Math.round(steep)}°`, "steep")}
      {meridian(flat, `Flat ${Math.round(flat)}°`, "flat")}
      {chips}

      {known && (
        <g className="eye-side">
          <text x={n > 0 ? W - 16 : 16} y={H - 14} textAnchor={n > 0 ? "end" : "start"}>Nasal</text>
          <text x={n > 0 ? 16 : W - 16} y={H - 14} textAnchor={n > 0 ? "start" : "end"}>Temporal</text>
        </g>
      )}
      <text x={W / 2} y={H - 14} textAnchor="middle" className="eye-caption">examiner's view · to scale</text>
    </svg>
  );
}
